<template>
    <v-card>
        <v-toolbar dense color="indigo" dark>
            <v-toolbar-title>{{ editedFactor.id ? 'Editar Fator' : 'Novo Fator' }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-icon @click="close">mdi-close</v-icon>
        </v-toolbar>
        <v-card-text class="pt-4">
            <div class="d-flex pt-4 mb-4">
                <div><strong>{{ $t('common.sector') }}: </strong>{{ sectorName }}</div>
                <v-spacer></v-spacer>
            </div> 
            <v-form ref="form" v-model="valid">
                <v-row dense>
                    <v-col cols="12" md="6">
                        <v-select
                            v-model="editedFactor.type"
                            :items="factorTypes"
                            label="Tipo de Fator"
                            item-text="text"
                            item-value="value"
                            outlined dense
                        ></v-select>
                    </v-col>
                    <v-col cols="12" md="6" v-if="editedFactor.id">
                        <v-text-field
                            v-model="editedFactor.code"
                            label="Código"
                            placeholder="Ex: FRT001"
                            outlined dense
                            disabled
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                        <v-text-field
                            v-model="editedFactor.description"
                            label="Descrição"
                            outlined dense
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-select
                            v-model="editedFactor.perspectiveId"
                            :items="perspectiveOptions"
                            item-text="name"
                            item-value="id"
                            label="Perspectiva"
                            outlined dense
                            clearable
                        ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-select
                            v-model="editedFactor.weight"
                            :items="weightOptions"
                            label="Peso Ponderado"
                            item-text="text"
                            item-value="value"
                            outlined dense
                            :hint="weightLegend"
                            persistent-hint
                        >
                            <template v-slot:item="{ item, on, attrs }">
                                <v-list-item v-bind="attrs" v-on="on">
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ item.legend }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                        </v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-select
                            v-model="editedFactor.rating"
                            :items="ratingOptions"
                            label="Classificação"
                            item-text="text"
                            item-value="value"
                            outlined dense
                            :hint="ratingLegend"
                            persistent-hint
                        >
                            <template v-slot:item="{ item, on, attrs }">
                                <v-list-item v-bind="attrs" v-on="on">
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ item.legend }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                        </v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field
                            :value="weightedResult"
                            label="Cálculo Automático"
                            outlined dense
                            readonly
                            filled
                            hint="Resultado ponderado = Peso × Nota"
                            persistent-hint
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                        <div class="text-caption grey--text text--darken-1">
                            Assim, o <strong>peso mede a importância do fator</strong> e a
                            <strong>nota mede a intensidade do impacto dele</strong>.
                        </div>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-menu
                            v-model="menuDate"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <!-- <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="editedFactor.validityDate"
                                    label="Data de Vigência"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    outlined dense
                                    v-bind="attrs"
                                    v-on="on"
                                ></v-text-field>
                            </template> -->
                            <v-date-picker v-model="editedFactor.validityDate" @input="menuDate = false"></v-date-picker>
                        </v-menu>
                    </v-col>
                    <v-col cols="12">
                        <v-textarea
                            v-model="editedFactor.comments"
                            label="Comentários/Observações"
                            outlined dense
                            rows="3"
                        ></v-textarea>
                    </v-col>
                    <v-col class="d-flex justify-end" cols="12">
                        <v-switch
                            v-model="editedFactor.active"
                            label="Status (Ativo/Inativo)"
                            color="success"
                        ></v-switch>
                    </v-col>
                </v-row>

                <v-divider class="my-4"></v-divider>
                
                <div v-if="editedFactor.history && editedFactor.history.length > 0">
                    <div class="text-subtitle-2 mb-2">Histórico de Alterações</div>
                    <v-simple-table dense class="grey lighten-5">
                        <thead>
                            <tr>
                                <th>Data</th>
                                <th>Usuário</th>
                                <th>Descrição</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(h, i) in editedFactor.history" :key="i">
                                <td>{{ h.date }}</td>
                                <td>{{ h.user }}</td>
                                <td>{{ h.description }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </div>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-btn v-if="editedFactor.id" color="red" text @click="deleteFactor">
                <v-icon left>mdi-delete</v-icon>Excluir
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="close">Cancelar</v-btn>
            <v-btn color="purple" dark @click="save">Salvar</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import translateService from '@/helper/translateService.js';

export default {
    name: 'FactorForm',
    props: {
        factor: {
            type: Object,
            default: null
        },
        sectorName: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            valid: true,
            menuDate: false,
            editedFactor: {
                id: null,
                type: '',
                code: '',
                description: '',
                weight: 1,
                rating: null,
                validityDate: new Date().toISOString().substr(0, 10),
                comments: '',
                active: true,
                perspectiveId: null,
                objectiveId: null,
                history: []
            },
            defaultFactor: {
                id: null,
                type: '',
                code: '',
                description: '',
                weight: 1,
                rating: null,
                validityDate: new Date().toISOString().substr(0, 10),
                comments: '',
                active: true,
                perspectiveId: null,
                objectiveId: null,
                history: []
            },
            factorTypes: [
                { text: 'Força', value: 'strength' },
                { text: 'Fraqueza', value: 'weakness' },
                { text: 'Oportunidade', value: 'opportunity' },
                { text: 'Ameaça', value: 'threat' }
            ],
            weightOptions: [
                { value: 1, text: '1 – Baixo', legend: 'Pouca importância estratégica' },
                { value: 2, text: '2 – Médio', legend: 'Importância estratégica moderada' },
                { value: 3, text: '3 – Alto', legend: 'Alta importância estratégica' }
            ],
            ratingOptions: [
                { value: 1, text: '1 – Muito baixo', legend: 'Impacto muito baixo' },
                { value: 2, text: '2 – Baixo', legend: 'Impacto baixo' },
                { value: 3, text: '3 – Médio', legend: 'Impacto moderado' },
                { value: 4, text: '4 – Alto', legend: 'Impacto alto' },
                { value: 5, text: '5 – Muito alto', legend: 'Impacto muito alto' }
            ],
        };
    },
    computed: {
        perspectiveOptions() {
            return (this.$store.state.current.perspectives || []).map(p => ({
                id: p.id,
                name: translateService.getName(p.names)
            }));
        },
        weightLegend() {
            const option = this.weightOptions.find(o => o.value === Number(this.editedFactor.weight));
            return option ? option.legend : 'Mede a importância estratégica do fator';
        },
        ratingLegend() {
            const option = this.ratingOptions.find(o => o.value === Number(this.editedFactor.rating));
            return option ? option.legend : 'Mede a intensidade do impacto do fator';
        },
        weightedResult() {
            const weight = Number(this.editedFactor.weight) || 0;
            const rating = Number(this.editedFactor.rating) || 0;
            return String(weight * rating);
        }
    },
    watch: {
        factor: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.editedFactor = JSON.parse(JSON.stringify(val));
                } else {
                    this.editedFactor = JSON.parse(JSON.stringify(this.defaultFactor));
                }
            }
        }
    },
    methods: {
        save() {
            // O backend gera id/código/histórico automaticamente.
            this.$emit('save', this.editedFactor);
        },
        deleteFactor() {
            this.$emit('delete', this.editedFactor.id);
        },
        close() {
            this.$emit('close');
        }
    }
};
</script>
